import { ApiClient } from 'ApiClient';
import {
  BoardTable,
  PosMenuItem,
  RestaurantTable,
  ServiceMode,
  TableOrder,
} from 'interfaces';

/**
 * Dine-in table ordering. Talks to the action-based /table-orders/* endpoints.
 * ApiClient adds `.json`, the Bearer token, and deep-camelCases the response,
 * and snake_cases POST body keys — so we pass plain camelCase objects.
 */
class TableOrderService {
  public getBoard(): Promise<BoardTable[]> {
    return ApiClient.get<BoardTable[]>('table-orders/board');
  }

  public getMenu(): Promise<PosMenuItem[]> {
    return ApiClient.get<PosMenuItem[]>('table-orders/menu');
  }

  public getTables(): Promise<RestaurantTable[]> {
    return ApiClient.get<RestaurantTable[]>('table-orders/tables');
  }

  public saveTable(data: Partial<RestaurantTable>): Promise<RestaurantTable> {
    return ApiClient.post<Partial<RestaurantTable>, RestaurantTable>(
      'table-orders/save-table',
      data,
    );
  }

  public deleteTable(id: number): Promise<{ deleted: number }> {
    return ApiClient.post<{ id: number }, { deleted: number }>(
      'table-orders/delete-table',
      { id },
    );
  }

  public open(restaurantTableId: number, covers?: number): Promise<TableOrder> {
    const payload: { restaurantTableId: number; covers?: number } = { restaurantTableId };
    if (covers !== undefined) payload.covers = covers;
    return ApiClient.post<typeof payload, TableOrder>('table-orders/open', payload);
  }

  public getOrder(id: number): Promise<TableOrder> {
    return ApiClient.get<TableOrder>(`table-orders/detail/${id}`);
  }

  public addItem(
    tableOrderId: number,
    menuItemId: number,
    quantity: number,
    course: number,
    notes?: string,
  ): Promise<TableOrder> {
    const payload: {
      tableOrderId: number;
      menuItemId: number;
      quantity: number;
      course: number;
      notes?: string;
    } = { tableOrderId, menuItemId, quantity, course };
    if (notes !== undefined && notes !== '') payload.notes = notes;
    return ApiClient.post<typeof payload, TableOrder>('table-orders/add-item', payload);
  }

  public updateItem(
    id: number,
    changes: { quantity?: number; notes?: string; course?: number },
  ): Promise<TableOrder> {
    return ApiClient.post<{ id: number }, TableOrder>('table-orders/update-item', {
      id,
      ...changes,
    });
  }

  public removeItem(id: number): Promise<TableOrder> {
    return ApiClient.post<{ id: number }, TableOrder>('table-orders/remove-item', { id });
  }

  public moveItem(id: number, course: number): Promise<TableOrder> {
    return ApiClient.post<{ id: number; course: number }, TableOrder>(
      'table-orders/move-item',
      { id, course },
    );
  }

  public removeCourse(tableOrderId: number, course: number): Promise<TableOrder> {
    return ApiClient.post<{ tableOrderId: number; course: number }, TableOrder>(
      'table-orders/remove-course',
      { tableOrderId, course },
    );
  }

  public voidOrder(tableOrderId: number): Promise<{ id: number; status: string }> {
    return ApiClient.post<{ tableOrderId: number }, { id: number; status: string }>(
      'table-orders/void',
      { tableOrderId },
    );
  }

  public fireCourse(
    tableOrderId: number,
    course: number,
  ): Promise<{ printed: boolean; fired?: number; reason?: string }> {
    return ApiClient.post<{ tableOrderId: number; course: number }, { printed: boolean; fired?: number; reason?: string }>(
      'table-orders/fire-course',
      { tableOrderId, course },
    );
  }

  public sendBar(
    tableOrderId: number,
  ): Promise<{ printed: boolean; sent?: number; reason?: string }> {
    return ApiClient.post<{ tableOrderId: number }, { printed: boolean; sent?: number; reason?: string }>(
      'table-orders/send-bar',
      { tableOrderId },
    );
  }

  public setMode(tableOrderId: number, serviceMode: ServiceMode): Promise<TableOrder> {
    return ApiClient.post<{ tableOrderId: number; serviceMode: ServiceMode }, TableOrder>(
      'table-orders/set-mode',
      { tableOrderId, serviceMode },
    );
  }

  public close(tableOrderId: number): Promise<{ id: number; status: string }> {
    return ApiClient.post<{ tableOrderId: number }, { id: number; status: string }>(
      'table-orders/close',
      { tableOrderId },
    );
  }
}

const singleton = new TableOrderService();
export { singleton as TableOrderService };
